import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  RadioGroup,
  Radio,
  FormControl,
  FormControlLabel,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import branding from '../../styles/branding';
import { AI_MODEL_OPTIONS } from '../../constants/aiModels';
import { PROMPT_TYPE_OPTIONS } from '../../constants/promptTypes';
import { OUTPUT_MODE_OPTIONS } from '../../constants/outputModes';

const LIGHT_BLUE = '#E3F2FD';
const LIGHT_BLUE_ACCENT = '#BBDEFB';
const LIGHT_BLUE_BORDER = '#90CAF9';

const SECTIONS = [
  { id: 'model', label: 'Model', icon: SmartToyOutlinedIcon },
  { id: 'prompt', label: 'Prompt', icon: DescriptionOutlinedIcon },
  { id: 'output', label: 'Output', icon: TuneOutlinedIcon },
];

const WidgetConfigModal = ({
  open,
  onClose,
  selectedModel,
  selectedPromptType,
  selectedOutputMode,
  onSaveConfig,
}) => {
  const [model, setModel] = useState(selectedModel || '');
  const [promptType, setPromptType] = useState(selectedPromptType || 'free_text');
  const [outputMode, setOutputMode] = useState(selectedOutputMode || 'elaborate');
  const [activeSection, setActiveSection] = useState('model');

  useEffect(() => {
    if (open) {
      setModel(selectedModel || '');
      setPromptType(selectedPromptType || 'free_text');
      setOutputMode(selectedOutputMode || 'elaborate');
      setActiveSection('model');
    }
  }, [open, selectedModel, selectedPromptType, selectedOutputMode]);

  const handleApply = () => {
    onSaveConfig?.({ model, promptType, outputMode });
    onClose();
  };

  const handleClose = () => {
    setModel(selectedModel || '');
    setPromptType(selectedPromptType || 'free_text');
    setOutputMode(selectedOutputMode || 'elaborate');
    onClose();
  };

  const selectedPromptOption = PROMPT_TYPE_OPTIONS.find((o) => o.value === promptType);
  const selectedOutputOption = OUTPUT_MODE_OPTIONS.find((o) => o.value === outputMode);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        ...branding.dialog?.PaperProps,
        sx: { minWidth: 420, minHeight: 340, borderRadius: 4, overflow: 'hidden', ...branding.dialog?.PaperProps?.sx }
      }}
      slotProps={branding.dialog?.slotProps}
    >
      <DialogTitle sx={{ pb: 0, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon />
          <Typography variant="h6">Choose configuration</Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'row', minHeight: 260 }}>
        {/* Left: settings list */}
        <Box
          sx={{
            width: 140,
            flexShrink: 0,
            borderRight: '1px solid rgba(0,0,0,0.12)',
            bgcolor: LIGHT_BLUE,
          }}
        >
          <List dense disablePadding sx={{ py: 0 }}>
            {SECTIONS.map(({ id, label, icon: Icon }) => (
              <ListItemButton
                key={id}
                selected={activeSection === id}
                onClick={() => setActiveSection(id)}
                sx={{
                  py: 1.5,
                  borderLeft: activeSection === id ? `3px solid ${LIGHT_BLUE_BORDER}` : '3px solid transparent',
                  bgcolor: activeSection === id ? LIGHT_BLUE_ACCENT : 'transparent',
                  '&.Mui-selected': {
                    bgcolor: LIGHT_BLUE_ACCENT,
                    '&:hover': { bgcolor: '#90CAF9' },
                  },
                  '&:hover': {
                    bgcolor: activeSection === id ? LIGHT_BLUE_ACCENT : 'rgba(187, 222, 251, 0.6)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Icon fontSize="small" sx={{ color: activeSection === id ? '#1976d2' : '#666' }} />
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: activeSection === id ? 600 : 400,
                    color: activeSection === id ? '#1976d2' : 'text.primary',
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
        {/* Right: options for selected setting */}
        <Box
          sx={{
            flex: 1,
            p: 2,
            bgcolor: '#FAFAFA',
            borderLeft: '1px solid rgba(0,0,0,0.12)',
          }}
        >
          {activeSection === 'model' && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary', fontWeight: 600 }}>
                AI Model
              </Typography>
              <RadioGroup value={model} onChange={(e) => setModel(e.target.value)}>
                {AI_MODEL_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio size="small" />}
                    label={opt.label}
                    sx={{ mb: 0.5 }}
                  />
                ))}
              </RadioGroup>
            </Box>
          )}
          {activeSection === 'prompt' && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary', fontWeight: 600 }}>
                Prompt type
              </Typography>
              <RadioGroup value={promptType} onChange={(e) => setPromptType(e.target.value)}>
                {PROMPT_TYPE_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio size="small" />}
                    label={opt.label}
                    sx={{ mb: 0.5 }}
                  />
                ))}
              </RadioGroup>
              {selectedPromptOption?.description && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, p: 1.25, bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 1 }}>
                  {selectedPromptOption.description}
                </Typography>
              )}
            </Box>
          )}
          {activeSection === 'output' && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary', fontWeight: 600 }}>
                Output style
              </Typography>
              <RadioGroup value={outputMode} onChange={(e) => setOutputMode(e.target.value)}>
                {OUTPUT_MODE_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio size="small" />}
                    label={opt.label}
                    sx={{ mb: 0.5 }}
                  />
                ))}
              </RadioGroup>
              {selectedOutputOption?.description && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, p: 1.25, bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 1 }}>
                  {selectedOutputOption.description}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid rgba(0,0,0,0.12)', gap: 1 }}>
        <Button onClick={handleClose} sx={branding.button?.sx}>
          Cancel
        </Button>
        <Button
          variant={branding.button.variant}
          size={branding.button.size}
          sx={branding.button.sx}
          onClick={handleApply}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WidgetConfigModal;
